import { useContext, useMemo, useState } from 'react';
import { ForecastContext } from '../../../contexts/investment-robot/ForecastContext';
import { toPrice } from '../../../utils/parsers';
import { Button } from '../../general/Button';
import { IconElements } from '../../../config/icons';
import { DisplaySmallError } from '../../general/DisplayError';
import { LoadingStateWrapper } from '../../general/LoadingStateWrapper';
import { useTranslation } from 'react-i18next';
import { ONBOARDING_LINKS } from '../../../config/constants';
import { GoalIndicationContext } from '../../../contexts/investment-robot/GoalIndicationContext';
import { useGetOnboardingLink } from '../../../api/investment-robot/useGetOnboardingLink';
import { RobotEnvironmentContext } from '../../../contexts/general/RobotEnvironmentContext';
import { Tooltip } from '../../general/Tooltip';
import { TooltipIndicator } from '../../general/TooltipIndicator';
import { EmailMeModal } from './email-me-modal/EmailMeModal';
import { useHistory } from 'react-router';

export const ForecastResultsSection: React.FC<{}> = () => {
    const { forecast } = useContext(ForecastContext);
    const { parameters } = useContext(GoalIndicationContext);
    const { mutateAsync: getOnboardingLink } = useGetOnboardingLink(forecast.parameters);
    const robotEnvironmentContext = useContext(RobotEnvironmentContext);
    const [isEmailMeModalShown, setIsEmailMeModalShown] = useState(false);
    const [isEmailSent, setIsEmailSent] = useState(false);
    const history = useHistory();

    const { t } = useTranslation('investment');
    const reachesGoal =
        forecast.query.data?.forecastExpirationValues.median &&
        forecast.parameters.capitalGoal.amount <= forecast.query.data?.forecastExpirationValues.median;

    const suggestions = useMemo(() => {
        const suggestedInitial =
            forecast.parameters.deposits.initial.amount + (forecast.query.data?.suggest?.paymentSingleAmount || 0);
        const suggestedPeriodicYearly =
            forecast.parameters.deposits.periodic.amount *
            (forecast.parameters.deposits.periodic.interval === 'monthly' ? 12 : 1) +
            (forecast.query.data?.suggest?.paymentRecurringAmountYearly ||
                forecast.query.data?.suggest?.paymentRecurringAmount ||
                0);
        return {
            initial: suggestedInitial,
            periodicYearly: suggestedPeriodicYearly,
        };
    }, [forecast.query.data]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <EmailMeModal 
                isShown={isEmailMeModalShown}
                onClose={() => setIsEmailMeModalShown(false)}
                setIsEmailSent={setIsEmailSent}
            />
            <div
                className="
            flex-1 pb-8 
            flex flex-col justify-center items-center
            bg-bnd-white-1 dark:bg-bnd-black-2 
            lg:rounded-2xl 
            shadow-2xl 
            overflow-hidden 
            "
            >
                <LoadingStateWrapper
                    isLoading={forecast.query.isLoading}
                    isFetching={forecast.query.isFetching}
                    isError={forecast.query.isError}
                    isIdle={forecast.query.isIdle}
                    loadingElement={null}
                    errorElement={<DisplaySmallError />}
                    className="flex-1 w-full"
                >
                    <div className="h-full flex flex-col">
                        <div className={`p-8 transition-colors  ${reachesGoal ? 'bg-bnd-green' : 'bg-bnd-orange'}`}>
                            {forecast.query.data?.forecastExpirationValues && (
                                <>
                                    <div
                                        className={`flex flex-col border-bnd-white-1 text-bnd-white-1 ${forecast.parameters.capitalGoal.amount > 0 && 'pb-4 border-b-2'
                                            }`}
                                    >
                                        <span className="block mt-2 text-xl">{t('forecast-screen-result-title')}</span>
                                        <span className="block mt-2 text-5xl font-semibold">
                                            {toPrice(forecast.query.data?.forecastExpirationValues.median, {
                                                onlyFullEuros: true,
                                                withCurrencySymbol: true,
                                            })}
                                        </span>
                                    </div>
                                    {forecast.parameters.capitalGoal.amount > 0 && reachesGoal && (
                                        <div className="flex flex-row items-center justify-center">
                                            <IconElements.Check className="flex-1 w-8 h-8 mr-2 text-white" />
                                            <p className={`mt-4 pl-2 text-xl text-bnd-white-1`} style={{ flex: 5 }}>
                                                {t('forecast-screen-result-description-positive')}
                                            </p>
                                        </div>
                                    )}
                                    {forecast.parameters.capitalGoal.amount > 0 && !reachesGoal && (
                                        <div className="flex flex-row items-center justify-center">
                                            <IconElements.Cross className="w-16 h-16 mr-2 text-white" />
                                            <p className="my-4 pl-2 text-xl text-bnd-white-1">
                                                {t('forecast-screen-result-description-negative')}
                                            </p>
                                        </div>
                                    )}
                                    {!reachesGoal && (
                                        <div className="pt-4 border-t-2 border-white text-white">
                                            <span className="font-extrabold text-base mb-2">
                                                {t('forecast-screen-calculation-suggestions-title-short')}
                                            </span>
                                            <ul className="mt-2 list-none text-sm">
                                                <li>
                                                    -{' '}
                                                    {forecast.parameters.deposits.periodic.interval === 'monthly'
                                                        ? t('forecast-screen-calculation-suggestions-periodic-month')
                                                        : t(
                                                            'forecast-screen-calculation-suggestions-periodic-year',
                                                        )}{' '}
                                                    <span className="font-bold">
                                                        {toPrice(
                                                            suggestions.periodicYearly /
                                                            (forecast.parameters.deposits.periodic.interval ===
                                                                'monthly'
                                                                ? 12
                                                                : 1),
                                                            { onlyFullEuros: true, withCurrencySymbol: true },
                                                        )}
                                                    </span>
                                                </li>
                                                <li>
                                                    - {t('forecast-screen-calculation-suggestions-initial')}{' '}
                                                    <span className="font-bold">
                                                        {toPrice(suggestions.initial, {
                                                            onlyFullEuros: true,
                                                            withCurrencySymbol: true,
                                                        })}{' '}
                                                    </span>
                                                </li>
                                                <li>- {t('forecast-screen-calculation-suggestions-duration')}</li>
                                                <li>- {t('forecast-screen-calculation-suggestions-strategy')}</li>
                                                <li>- {t('forecast-screen-calculation-suggestions-capital-goal')}</li>
                                            </ul>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className={`flex flex-col px-8 pt-4 justify-between `}>
                            {forecast.query.data?.forecastExpirationValues && (
                                <div className="flex flex-col">
                                    <div className="flex flex-col xl:flex-row justify-between xl:items-center py-4 border-b border-gray-300 dark:border-gray-700">
                                        <div className="flex flex-row items-center justify-between">
                                            <span className="block text-base text-gray-800">
                                                {t('forecast-screen-result-optimistic-market')}
                                            </span>
                                            <Tooltip
                                                tooltipId="optimistic"
                                                tooltipTitle={t('forecast-screen-result-optimistic-market')}
                                                tooltipText={t('forecast-screen-result-optimistic-market-tooltip')}
                                                className=""
                                            >
                                                <TooltipIndicator>?</TooltipIndicator>
                                            </Tooltip>
                                        </div>
                                        <span className="block text-2xl font-semibold">
                                            {toPrice(forecast.query.data?.forecastExpirationValues.optimistic, {
                                                onlyFullEuros: true,
                                                withCurrencySymbol: true,
                                            })}
                                        </span>
                                    </div>
                                    <div className="flex flex-col xl:flex-row justify-between xl:items-center py-4 border-b border-gray-300 dark:border-gray-700">
                                        <div className="flex flex-row items-center justify-between">
                                            <span className="text-base text-gray-800">
                                                {t('forecast-screen-result-expected-market')}
                                            </span>
                                            <Tooltip
                                                tooltipId="expected"
                                                tooltipTitle={t('forecast-screen-result-expected-market')}
                                                tooltipText={t('forecast-screen-result-expected-market-tooltip')}
                                                className=""
                                            >
                                                <TooltipIndicator>?</TooltipIndicator>
                                            </Tooltip>
                                        </div>
                                        <span
                                            className={`block text-2xl font-semibold transition-colors ${reachesGoal ? 'text-bnd-black-1' : 'text-bnd-orange'
                                                }`}
                                        >
                                            {toPrice(forecast.query.data?.forecastExpirationValues.median, {
                                                onlyFullEuros: true,
                                                withCurrencySymbol: true,
                                            })}
                                        </span>
                                    </div>
                                    <div className="flex flex-col xl:flex-row justify-between xl:items-center py-4 border-b border-gray-300 dark:border-gray-700">
                                        <div className="flex flex-row items-center justify-between">
                                            <span className="block text-base text-gray-800">
                                                {t('forecast-screen-result-pessimistic-market')}
                                            </span>
                                            <Tooltip
                                                tooltipId="pessimistic"
                                                tooltipTitle={t('forecast-screen-result-pessimistic-market')}
                                                tooltipText={t('forecast-screen-result-pessimistic-market-tooltip')}
                                                className=""
                                            >
                                                <TooltipIndicator>?</TooltipIndicator>
                                            </Tooltip>
                                        </div>
                                        <span className="block text-2xl font-semibold">
                                            {toPrice(forecast.query.data?.forecastExpirationValues.pessimistic, {
                                                onlyFullEuros: true,
                                                withCurrencySymbol: true,
                                            })}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={`flex flex-col px-8 pt-4 justify-between `}>
                            <div className="flex flex-col">
                                <Button
                                    className="mt-8 truncate shadow-lg"
                                    onClick={() => {
                                        setIsEmailMeModalShown(true);
                                    }}
                                >
                                    <div className="flex flex-row justify-center items-center">
                                        <IconElements.Banner className="w-6 h-6 mr-2 " />
                                        {t('forecast-screen-result-save-calculation')}
                                    </div>
                                </Button>
                                {isEmailSent && (
                                    <div className="flex flex-row justify-center items-center text-bnd-green mt-4">
                                        {t('forecast-screen-result-email-sent')}
                                    </div>
                                )}
                                {robotEnvironmentContext.environment !== 'Advisor' && (
                                    <Button
                                        className="mt-8 truncate"
                                        tint="pink"
                                        onClick={async () => {
                                            if (parameters.selectedGoal === 'children') {
                                                window.location.href = ONBOARDING_LINKS.CHILDREN;
                                            } else {
                                                const url = await getOnboardingLink();
                                                window.location.href = url;
                                            }
                                        }}
                                    >
                                        <span className="flex-1 text-center">
                                            {t('forecast-screen-result-section-become-customer')}
                                        </span>
                                    </Button>
                                )}
                                <Button
                                    tint="sober"
                                    className="mt-8"
                                    onClick={() => {
                                        history.push('/');
                                    }}
                                >
                                    Begin opnieuw
                                </Button>
                            </div>
                        </div>
                    </div>
                </LoadingStateWrapper>
            </div>
        </>
    );
};
